'use client';

import { SEARCH_TYPE } from '@/constants/searchType';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Button, IconButton, styled } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useDatadogTracker } from '@/hooks/publicSearch/datadog/useDatadogTracker';
import { EventType } from '@/lib/types';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSearchPrecheck } from '@/hooks/publicSearch/useSearchPrecheck';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { LogicalOperator } from '@/components/publicSearch/constants';
import { CircularProgress, TextField } from '@/primitives';
import { QUICK_SEARCH_DEBOUNCE_MS } from '../search/quickSearchConfig';
import { getHeroSearchPlaceholder } from '../search/quickSearchCopy';
import { getHeroSearchGuidance } from '../search/heroSearchGuidance';
import HeroSearchGuidanceChip from '../search/HeroSearchGuidanceChip';
import {
  isDisplayableEbvConfig,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { armSearchHistoryPush } from '@/hooks/publicSearch/usePublicSearchUrlSync';
import { useAtom, useSetAtom } from 'jotai';
import {
  aiSearchLoadingAtom,
  openAdvanceFilterDialogAtom,
} from '../search/components/AdvancedFilterDialog';
import SavedFiltersDropdown from './SavedFiltersDropdown';
import {
  publicSearchEndAdornmentHeightPx,
  quickSearchInputSx,
} from '../search/constants';
import { supportsSavedFilters } from '@/publicSearch/store/useSavedFiltersStore';
import { useAiSearchEnabled } from '@/publicSearch/graphql/userPreference/hooks';
import { normalizeAiSearchFilterGroups } from '@/components/publicSearch/search/normalizeAiSearchFilters';
import {
  beginAiSearchRequest,
  cancelPendingAiSearch,
  finishAiSearchRequest,
  hasPendingAiSearchRequest,
  isCurrentAiSearchRequest,
} from '@/hooks/publicSearch/aiSearchRequest';
import { enqueueSnackbar } from 'notistack';
import { SNACKBAR_VARIANT } from '@/constants/notifications';

const glowSpin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const AI_SUGGESTIONS: Record<string, string[]> = {
  [SEARCH_TYPE.ANIMAL]: [
    'Find all animals from a specific herd prefix',
    'Show me animals with a name starting with Doreen',
    'Look up an animal by its tag or tattoo number',
    'Which animal has registration number ABC123?',
    'Find animals with identifier starting with XY',
    'Which bulls have the best calving ease EBVs?',
    'Find females with above-average milk EBVs',
    'Show animals with low birth weight and high 600-day weight',
    'Find top EBV sires for growth traits',
  ],
  [SEARCH_TYPE.MEMBERS]: [
    'Find a breeder by their stud name',
    'Look up a member by their membership number',
    'Show me all members named Smith',
    'Which stud has prefix XYZ?',
  ],
  [SEARCH_TYPE.SALE_CATALOGUES]: [
    'Find the Spring Bull Sale catalogue',
    'Show me catalogues for the 2025 season',
    'Look up a sale by its name',
    'Which catalogues match this keyword?',
  ],
  default: [
    'Search by name or identifier',
    'Look up a specific record by ID',
    'Find entries starting with a keyword',
    'Type a name to search the database',
  ],
};

const SearchCountPillButton = styled(Button)(({ theme: _theme }) => ({
  boxSizing: 'border-box',
  height: publicSearchEndAdornmentHeightPx,
  minHeight: publicSearchEndAdornmentHeightPx,
  maxHeight: publicSearchEndAdornmentHeightPx,
  paddingBlock: '2px',
  paddingInline: '10px',
  marginInlineStart: 1,
  marginInlineEnd: 5,
  borderRadius: '4px',
  fontSize: '13px',
  lineHeight: 1.25,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  minWidth: 'max-content',
  // Match flat saved-filters control (no elevation); contained variant adds shadow by default
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': { boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
  '&.Mui-focusVisible': { boxShadow: 'none' },
  '&.Mui-disabled': { boxShadow: 'none' },
}));

interface PublicSearchInputProps {
  variant?: 'default' | 'mobile';
}

export default function PublicSearchInput({
  variant = 'default',
}: PublicSearchInputProps) {
  const theme = useTheme();
  const aiAccent = theme.palette.publicSearch.aiAccent;
  const aiAccentDark = theme.palette.publicSearch.aiAccentDark;
  const aiAccentSoft = alpha(aiAccent, 0.5);
  const isMobileVariant = variant === 'mobile';

  const searchType = useSearchType();
  const {
    filters,
    filterGroups,
    setFilterGroups,
    setGroupsOperator,
    clearFilter,
    clearFilterGroups,
  } = useFilterStore(searchType);
  const society = usePublicSearchContext((context) => context.society);
  const orgId = society.orgId;
  const {
    basicSearchInput,
    setOpenResult,
    setBasicSearch,
    isAiMode,
    setIsAiMode,
    aiSearchPrompt,
    setAiSearchPrompt,
    setSearchDisplayTerm,
    setHeroSearchDraft,
  } = useSearchStore();
  const { aiSearchEnabled } = useAiSearchEnabled();
  const setOpenAdvancedDialog = useSetAtom(openAdvanceFilterDialogAtom);
  const { paginationModel, setPaginationModel } = useGridStore();
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchStartTimeRef = useRef<number | null>(null);
  const query = useSearch();
  const searchCount = query.data?.totalCount;
  const searchActive = query.searchActive;
  const { errorShown, showError, hideError } = useSearchPrecheck(
    searchActive && typeof searchCount === 'undefined',
  );
  const initialValue = useRef<string | null>(basicSearchInput);

  const placeholder = useMemo(
    () => getHeroSearchPlaceholder(searchType),
    [searchType],
  );

  const [value, setValue] = useState<string>(basicSearchInput);

  // Mobile keeps the chip in this component; desktop renders it in SearchHeader
  // above the toolbar row so it stays inside the card (not under the AppBar).
  const mobileGuidance = useMemo(() => {
    if (!isMobileVariant || isAiMode) return null;
    return getHeroSearchGuidance(value, searchType);
  }, [isMobileVariant, isAiMode, value, searchType]);
  const [aiLoading, setAiLoading] = useAtom(aiSearchLoadingAtom);
  const [aiSuggestionIndex, setAiSuggestionIndex] = useState<number>(0);

  useEffect(() => () => cancelPendingAiSearch(), []);

  // Keep a live draft for desktop guidance in SearchHeader (not debounced).
  useEffect(() => {
    setHeroSearchDraft(value);
  }, [value, setHeroSearchDraft]);

  // True while the box still shows the last submitted AI prompt; focus/click
  // then selects it all so typing replaces it (no back-to-back concatenation).
  const shouldSelectPrompt = isAiMode && !!value && value === aiSearchPrompt;

  const ebvFields = useMemo(() => {
    const config = parseEbvConfig(society.settings);
    return config.filter(isDisplayableEbvConfig).map((item) => ({
      field: item.field,
      label: item.label.replace(/\^/g, ' '),
      category: item.category,
    }));
  }, [society.settings]);

  const aiSuggestions = useMemo(() => {
    const key = Object.keys(AI_SUGGESTIONS).includes(searchType)
      ? searchType
      : 'default';
    return AI_SUGGESTIONS[key]!;
  }, [searchType]);

  const getAiPlaceholder = useCallback(
    () => aiSuggestions[aiSuggestionIndex % aiSuggestions.length]!,
    [aiSuggestions, aiSuggestionIndex],
  );

  useEffect(() => {
    if (!isAiMode) {
      setAiSuggestionIndex(0);
      return;
    }
    const interval = setInterval(
      () => setAiSuggestionIndex((i) => i + 1),
      3000,
    );
    return () => clearInterval(interval);
  }, [isAiMode]);

  const { trackEvent } = useDatadogTracker();

  const refocusInputWhenNoResult = () => {
    if (searchCount !== 0 || query.loading) return;
    if (!inputRef.current) return;

    inputRef.current.focus();
  };

  const errorCheck = () => {
    if (searchActive && typeof searchCount === 'undefined' && !errorShown) {
      return showError();
    }

    if ((!searchActive || typeof searchCount !== 'undefined') && errorShown) {
      hideError();
    }
  };

  const handleSearch = () => {
    let error = errorCheck();
    if (!!error) return;

    hideError();
    armSearchHistoryPush(); // committed search → own Back entry
    searchStartTimeRef.current = performance.now();
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setOpenResult(true);
    inputRef.current?.blur();
    setTimeout(refocusInputWhenNoResult, 1200);

    // ensure pagination is reset before search is triggered
    setTimeout(query.startSearch, 10);
  };

  const handleAiSearch = async () => {
    const submittedPrompt = value.trim();
    if (!submittedPrompt || aiLoading) return;
    setAiLoading(true);
    const request = beginAiSearchRequest();

    // Arm before clearing: the clear is the first write, so it pushes a fresh
    // Back entry and leaves the previous committed search's URL intact.
    armSearchHistoryPush();
    clearFilter();
    clearFilterGroups();
    setOpenAdvancedDialog(true);

    const aiStartedAt = performance.now();

    const runNonAiSearchFallback = () => {
      trackEvent?.(
        'AiSearchFailed',
        {
          searchType,
          durationMs: Math.round(performance.now() - aiStartedAt),
          orgId,
        },
        EventType.FORM_SUBMIT,
      );
      handleSearch();
    };

    try {
      const res = await fetch('/api/ai-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: request.controller.signal,
        body: JSON.stringify({
          query: submittedPrompt,
          searchType,
          ebvFields: ebvFields.length > 0 ? ebvFields : undefined,
          societyCode: society?.societyCode,
          orgId,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!isCurrentAiSearchRequest(request)) return;

      // Auth and rate-limit failures get explicit, actionable feedback rather
      // than a silent non-AI fallback that drops the user's intent.
      if (!res.ok) {
        if (res.status === 401 || res.status === 429) {
          trackEvent?.(
            'AiSearchFailed',
            {
              searchType,
              failureStatus: res.status,
              durationMs: Math.round(performance.now() - aiStartedAt),
              orgId,
            },
            EventType.FORM_SUBMIT,
          );
          setOpenAdvancedDialog(false);
          const message =
            res.status === 401
              ? 'Please sign in to use AI search.'
              : (data?.error ??
                'Too many AI search requests. Please wait and try again.');
          enqueueSnackbar(message, {
            variant: SNACKBAR_VARIANT.WARNING,
            autoHideDuration: 8000,
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
          return;
        }
        runNonAiSearchFallback();
        return;
      }
      if (data?.error) {
        runNonAiSearchFallback();
        return;
      }

      // The AI judged the query as not a runnable search (invalid / unsupported
      // / ambiguous). Surface the reason and stop — don't silently fall back.
      if (data.status && data.status !== 'ok') {
        trackEvent?.(
          'AiSearchRejected',
          {
            searchType,
            rejectStatus: data.status,
            durationMs: Math.round(performance.now() - aiStartedAt),
            orgId,
          },
          EventType.FORM_SUBMIT,
        );
        setOpenAdvancedDialog(false);
        const message = data.suggestion
          ? `${data.reason} ${data.suggestion}`
          : data.reason;
        enqueueSnackbar(message, {
          variant: SNACKBAR_VARIANT.WARNING,
          autoHideDuration: 8000,
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        return;
      }

      setFilterGroups(normalizeAiSearchFilterGroups(data.filterGroups));
      setGroupsOperator(
        data.groupsOperator === LogicalOperator.or
          ? LogicalOperator.or
          : LogicalOperator.and,
      );
      setAiSearchPrompt(submittedPrompt);
      // Keep the prompt visible so it's clear what the results are for.
      initialValue.current = submittedPrompt;
      setSearchDisplayTerm(submittedPrompt);
      setValue(submittedPrompt);
      searchStartTimeRef.current = performance.now();
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      setOpenResult(true);
      inputRef.current?.blur();
      // No manual startSearch: the new filterGroups change useSearch's signature
      // and auto-fire it; a manual call here would run with stale filters.

      // Track success only after all follow-up state updates have completed, so
      // a throw in that work can't record both success and failure for one
      // attempt (the shared catch also emits AiSearchFailed).
      trackEvent?.(
        'AiSearchExecuted',
        {
          searchType,
          filterGroupCount: data.filterGroups?.length,
          durationMs: Math.round(performance.now() - aiStartedAt),
          orgId,
        },
        EventType.FORM_SUBMIT,
      );
    } catch {
      if (!isCurrentAiSearchRequest(request)) return;
      runNonAiSearchFallback();
    } finally {
      finishAiSearchRequest(request);
      if (!hasPendingAiSearchRequest()) setAiLoading(false);
    }
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      if (isAiMode && searchType === SEARCH_TYPE.ANIMAL) {
        handleAiSearch();
      } else if (!isAiMode) {
        handleSearch();
      }
    }
  };

  const serializedFilters = useMemo(() => JSON.stringify(filters), [filters]);
  const serializedFilterGroups = useMemo(
    () => JSON.stringify(filterGroups),
    [filterGroups],
  );

  const hasGroupedFilters = useMemo(
    () => filterGroups.some((g) => g.filters.length > 0),
    [filterGroups],
  );

  useEffect(() => {
    // Reset to page 0 when filters / pageSize change. Read the current page from
    // the store, NOT the `paginationModel` prop: adding `paginationModel.page` to
    // the deps would make "Next page" re-fire this effect and snap back to 0. The
    // early-return when already on page 0 avoids writing a fresh paginationModel
    // reference, which would invalidate the list hooks' startSearch callbacks and
    // cause a duplicate POST per cross-page nav.
    if (useGridStore.getState().paginationModel.page === 0) return;
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  }, [
    paginationModel.pageSize,
    serializedFilters,
    serializedFilterGroups,
    setPaginationModel,
  ]);

  useEffect(() => {
    if (
      searchStartTimeRef.current !== null &&
      !query.loading &&
      typeof searchCount === 'number' &&
      orgId
    ) {
      const durationMs = performance.now() - searchStartTimeRef.current;

      trackEvent?.(
        'SearchExecuted',
        {
          searchType,
          searchTerm: basicSearchInput,
          filterCount: filters.length,
          resultCount: searchCount,
          triggeredFrom: 'basic',
          orgId,
          durationMs: Math.round(durationMs),
        },
        EventType.FORM_SUBMIT,
      );
      searchStartTimeRef.current = null;
    }
  }, [
    basicSearchInput,
    filters.length,
    orgId,
    query.loading,
    searchCount,
    searchType,
    trackEvent,
  ]);

  useEffect(() => {
    if (isAiMode) return;
    // prevent triggering on initial render
    if (value === initialValue.current) return;
    initialValue.current = null;
    const timeoutId = setTimeout(
      () => setBasicSearch(value),
      QUICK_SEARCH_DEBOUNCE_MS,
    );

    return () => clearTimeout(timeoutId);
  }, [value, setBasicSearch, isAiMode]);

  // Clear input on clear-filters / tab switch. Skip in AI mode: AI uses grouped
  // filters, so an empty `filters` here is normal and must not wipe the prompt.
  useEffect(() => {
    if (isAiMode) return;
    if (filters.length) return;
    initialValue.current = null;
    setValue('');
  }, [filters, isAiMode]);

  // Sync input value when basicSearchInput changes from external source (e.g.,
  // Basic Search Apply clearing the hero). Arm initialValue so the debounce
  // effect does not call setBasicSearch('') and wipe applied filters.
  useEffect(() => {
    if (basicSearchInput !== value) {
      initialValue.current = basicSearchInput;
      setValue(basicSearchInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicSearchInput]);

  const renderButtonLabel = () => {
    if (aiLoading) {
      return <CircularProgress size={16} sx={{ color: 'common.white' }} />;
    }

    if (isAiMode && searchType === SEARCH_TYPE.ANIMAL) {
      return 'AI Search';
    }

    if (searchActive && (query.loading || typeof searchCount === 'undefined')) {
      return (
        <>
          <CircularProgress size={12} sx={{ color: 'inherit', mr: 0.75 }} />
          Updating results
        </>
      );
    }

    if (!basicSearchInput && filters.length === 0 && !hasGroupedFilters) {
      return 'Show all results';
    }

    if (typeof searchCount === 'number' && searchCount === 0) {
      return '0 results found';
    }

    if (typeof searchCount === 'number') {
      return `Show ${searchCount.toLocaleString()} results`;
    }

    return 'Show results';
  };

  const renderResultsControl = () => {
    if (!supportsSavedFilters(searchType)) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <SearchCountPillButton
          color="info"
          disabled={
            isAiMode && searchType === SEARCH_TYPE.ANIMAL
              ? aiLoading || !value.trim()
              : searchActive &&
                (query.loading || typeof searchCount === 'undefined')
          }
          variant="contained"
          onClick={
            isAiMode && searchType === SEARCH_TYPE.ANIMAL
              ? handleAiSearch
              : handleSearch
          }
          sx={{
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            marginInlineEnd: 0,
            ...(isMobileVariant && { paddingInlineEnd: '4px' }),
          }}
        >
          {renderButtonLabel()}
        </SearchCountPillButton>
        <SavedFiltersDropdown
          containerRef={containerRef}
          compact={isMobileVariant}
        />
      </Box>
    );
  };

  const renderButton = () => {
    if (isMobileVariant) {
      const resultsControl = renderResultsControl();

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          {value ? (
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => {
                hideError();
                initialValue.current = null;
                setValue('');
                if (basicSearchInput) {
                  // Push a clean Back entry so the cleared search is preserved
                  // one step back (matches clearing via the keyboard).
                  armSearchHistoryPush();
                  setBasicSearch('');
                  setPaginationModel({
                    page: 0,
                    pageSize: paginationModel.pageSize,
                  });
                  setOpenResult(true);
                  setTimeout(query.startSearch, 10);
                }
              }}
              sx={{
                mr: 0.25,
                color: 'text.secondary',
                '& svg': { fontSize: 16 },
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
          {resultsControl}
        </Box>
      );
    }

    return renderResultsControl();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: isMobileVariant ? 'column' : 'row',
        alignItems: isMobileVariant ? 'stretch' : 'center',
      }}
    >
      {mobileGuidance ? (
        <HeroSearchGuidanceChip
          guidance={mobileGuidance}
          sx={{ alignSelf: 'flex-start', mb: 0.75, order: -1 }}
        />
      ) : null}
      {isAiMode && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: '-3px',
            borderRadius: '11px',
            overflow: 'hidden',
            zIndex: 0,
            filter: 'blur(4px)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '200%',
              aspectRatio: '1',
              background: `conic-gradient(from 0deg, ${aiAccent}, ${aiAccentDark}, ${aiAccentSoft}, transparent 30%, transparent 70%, ${aiAccentSoft}, ${aiAccentDark}, ${aiAccent})`,
              animation: `${glowSpin} 3s linear infinite`,
            }}
          />
        </Box>
      )}
      <TextField
        fullWidth
        slotProps={{
          input: {
            sx: isMobileVariant
              ? { pl: 0.5, pr: value ? 0.25 : 1 }
              : { px: 0.5 },
            startAdornment: isMobileVariant ? (
              <IconButton
                size="small"
                aria-label="Search"
                disabled={aiLoading}
                onClick={() => {
                  if (isAiMode && searchType === SEARCH_TYPE.ANIMAL) {
                    void handleAiSearch();
                    return;
                  }
                  handleSearch();
                }}
                sx={{
                  color: 'text.secondary',
                  '& svg': { fontSize: 16 },
                }}
              >
                <SearchOutlinedIcon />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                disabled={aiLoading}
                onClick={() => {
                  if (!isAiMode && !aiSearchEnabled) return;
                  if (isAiMode) setAiSearchPrompt('');
                  setIsAiMode(!isAiMode);
                }}
                sx={{ '& svg': { fontSize: '1.125rem' } }}
              >
                {(() => {
                  if (aiLoading) {
                    return (
                      <CircularProgress size={18} sx={{ color: aiAccent }} />
                    );
                  }
                  if (isAiMode) {
                    return (
                      <AutoAwesomeIcon
                        sx={{
                          color: aiAccent,
                          filter: `drop-shadow(0 0 4px ${aiAccentSoft})`,
                        }}
                      />
                    );
                  }
                  return <TravelExploreIcon />;
                })()}
              </IconButton>
            ),
            endAdornment: renderButton(),
          },
        }}
        inputRef={inputRef}
        autoFocus
        placeholder={isAiMode ? getAiPlaceholder() : placeholder}
        sx={[
          { flex: 1, minWidth: 0 },
          !isMobileVariant && quickSearchInputSx,
          isMobileVariant
            ? {
                position: 'relative',
                zIndex: 1,
                width: '100%',
                textTransform: 'none',
                '& .MuiInputBase-root': {
                  minWidth: 0,
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    border: '0.5px solid',
                    borderColor: (theme) => theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) => theme.palette.text.secondary,
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '1px',
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                '& input': {
                  minWidth: 0,
                  padding: '10px 0',
                  height: '20px',
                  fontSize: '16px', // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&::placeholder': {
                    color: 'text.secondary',
                    opacity: 1,
                  },
                },
              }
            : {
                position: 'relative',
                zIndex: 1,
                height: '38px',
                width: '100%',
                textTransform: 'none',
                '& .MuiInputBase-root': {
                  minWidth: 0,
                  '& fieldset': {
                    border: '1.5px solid',
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) => theme.palette.primary.dark,
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                '& input': {
                  minWidth: 0,
                  padding: '8px 12px 8px 0px',
                  height: '20px',
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:placeholder-shown': {
                    textOverflow: 'ellipsis',
                  },
                },
                fieldset: {
                  height: '44px',
                },
              },
          isAiMode && {
            '& .MuiInputBase-root': {
              '& fieldset': {
                border: `2px solid ${aiAccent}`,
              },
            },
            '& input::placeholder': {
              color: aiAccent,
              opacity: 0.7,
              transition: 'opacity 0.3s ease',
            },
          },
        ]}
        value={value}
        onChange={(e) => {
          const nextValue = e.target.value;
          // Arm a history push whenever the box's emptiness flips
          if (!isAiMode && !value !== !nextValue) armSearchHistoryPush();
          setValue(nextValue);
        }}
        onKeyDown={handleInputKeyPress}
        onFocus={() => {
          if (shouldSelectPrompt) inputRef.current?.select();
        }}
      />
    </Box>
  );
}
